import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, Users, Shield, Calendar, Sparkles, Star, ChevronRight, ChevronDown, ChevronLeft, Instagram, Mail, Baby, Accessibility, CheckCircle, AlertTriangle, MessageCircle, X, Building2, Stethoscope, GraduationCap, ShoppingBag, Globe, MapPin, Hand, Download, Gem, UsersRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import InstallPrompt from "@/components/InstallPrompt";
import EventsTicker from "@/components/EventsTicker";
import NiamhsCorner from "@/pages/NiamhsCorner";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_4791deb4-db22-4895-9e0a-21aa9f309cff/artifacts/j9ko1qni_70964783-6509-4567-B714-2BDADDC4579E%20%281%29.PNG";

// Carousel images of families and activities - diverse representation including African, Caribbean, mixed heritage
const CAROUSEL_IMAGES = [
  {
    url: "https://images.unsplash.com/photo-1643818650011-93880bbe1ec2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwbXVsdGlyYWNpYWwlMjBmYW1pbHklMjBjaGlsZHJlbiUyMHBsYXlpbmclMjBwYXJrJTIwaGFwcHl8ZW58MHx8fHwxNzg0NTkwMzQ5fDA&ixlib=rb-4.1.0&q=85",
    alt: "African Caribbean family with children playing in park",
    caption: "Family adventures together"
  },
  {
    url: "https://images.pexels.com/photos/8457809/pexels-photo-8457809.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Diverse group of children playing joyfully outdoors",
    caption: "Different but the Same!"
  },
  {
    url: "https://images.pexels.com/photos/10037043/pexels-photo-10037043.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Mixed heritage family playing tug of war outdoors",
    caption: "Family fun day"
  },
  {
    url: "https://images.unsplash.com/photo-1617855080812-2d84e040ef70?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxibGFjayUyMGZhbWlseSUyMG91dGRvb3IlMjBjaGlsZHJlbiUyMHBsYXlpbmclMjBwaWNuaWN8ZW58MHx8fHwxNzg0NTkwMzU0fDA&ixlib=rb-4.1.0&q=85",
    alt: "Children of different backgrounds playing together",
    caption: "Playdate meetups"
  },
  {
    url: "https://images.unsplash.com/photo-1646250852980-e2beba095e97?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxjaGlsZCUyMHdoZWVsY2hhaXIlMjBoYXBweSUyMHNtaWxpbmclMjBwbGF5aW5nJTIwZnVufGVufDB8fHx8MTc4MjczODY4NXww&ixlib=rb-4.1.0&q=85",
    alt: "Happy smiling boy with special needs",
    caption: "Inclusive play"
  }
];

const EXPANSION_COUNTRIES = [
  { code: "ireland", name: "Ireland", flag: "🇮🇪" },
  { code: "canada", name: "Canada", flag: "🇨🇦" },
  { code: "australia", name: "Australia", flag: "🇦🇺" },
  { code: "uae", name: "UAE", flag: "🇦🇪" },
  { code: "south_africa", name: "South Africa", flag: "🇿🇦" },
  { code: "new_zealand", name: "New Zealand", flag: "🇳🇿" },
  { code: "singapore", name: "Singapore", flag: "🇸🇬" },
];

const Landing = () => {
  const [activeMenu, setActiveMenu] = useState(null);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [showInternationalModal, setShowInternationalModal] = useState(false);
  const [intlEmail, setIntlEmail] = useState("");
  const [intlCountry, setIntlCountry] = useState("");
  const [intlSubmitting, setIntlSubmitting] = useState(false);
  const [weeklyHighlights, setWeeklyHighlights] = useState([]);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);

  // Fetch weekly highlights for landing page
  useEffect(() => {
    const fetchHighlights = async () => {
      try {
        const response = await axios.get(`${API}/community/weekly-highlights?limit=6`);
        setWeeklyHighlights(response.data.highlights || []);
      } catch (error) {
        // Silent fail for landing page
      }
    };
    fetchHighlights();
  }, []);

  const handleInternationalSubmit = async () => {
    if (!intlEmail || !intlCountry) {
      toast.error("Please enter your email and select a country");
      return;
    }
    setIntlSubmitting(true);
    try {
      await axios.post(`${API}/international/interest`, {
        email: intlEmail,
        country: intlCountry
      });
      toast.success("Thanks! We'll notify you when KiddiPlay launches in your country!");
      setShowInternationalModal(false);
      setIntlEmail("");
      setIntlCountry("");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIntlSubmitting(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) {
      toast.error("Please enter your email");
      return;
    }
    setNewsletterSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email: newsletterEmail
      });
      toast.success("You're subscribed! Look out for updates from KiddiPlay 💜");
      setNewsletterEmail("");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  // Auto-rotate carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % CAROUSEL_IMAGES.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const menuItems = [
    {
      id: "unique-to-us",
      label: "Unique to Us",
      icon: Gem,
      color: "from-purple-500 to-pink-500",
      description: "Discover features you won't find anywhere else - designed specifically for UK families.",
      features: ["MummaMate - Mutual family aid", "BuddyUp - Share activities & costs", "Naughty Step - Conflict resolution", "Safety Hub - Trusted connections"],
      isDropdown: true,
      subItems: [
        {
          id: "mummamate",
          label: "MummaMate™",
          icon: Heart,
          color: "from-pink-500 to-rose-500",
          description: "Your neighbourhood support network. Need a pharmacy run? School pickup help? Connect with trusted families who've got your back when life gets hectic.",
          features: ["Emergency school pickups", "Pharmacy & shopping runs", "Neighbours helping neighbours", "No money changes hands"]
        },
        {
          id: "buddy-up",
          label: "Buddy UP!",
          icon: Baby,
          color: "from-orange-500 to-amber-500",
          description: "Team up with trusted families to share activities and split costs! Perfect for tutoring sessions, language classes, quirky hobbies like pottery or coding, sports clubs, and so much more.",
          features: ["Split activity costs 50/50", "Share tutoring & language classes", "Join quirky hobby clubs together", "Sports, STEM, arts & more!"]
        },
        {
          id: "naughty-step",
          label: "Naughty Step!",
          icon: AlertTriangle,
          color: "from-amber-500 to-orange-500",
          description: "Our unique conflict resolution system. Had a disagreement? We help families work it out fairly and keep the community positive.",
          features: ["Fair mediation process", "Anonymous reporting", "Community guidelines", "Fresh start options"]
        },
        {
          id: "safety-hub",
          label: "Safety Hub",
          icon: Shield,
          color: "from-teal-500 to-green-500",
          description: "Your peace of mind matters. Trusted Family Badges, safe meetup check-ins, and a community that looks out for each other.",
          features: ["Trusted Family Badges", "Meetup check-ins", "Verified profiles", "Report & block tools"]
        }
      ]
    },
    {
      id: "community",
      label: "Community",
      icon: UsersRound,
      color: "from-rose-500 to-rose-600",
      description: "A trusted space built on genuine connections. Verified profiles, easy blocking, and a community that looks out for each other.",
      features: ["Verified profiles", "Block & report", "Public meetup tips", "Community guidelines"]
    },
    {
      id: "find-people",
      label: "Let's Match Up!",
      icon: Users,
      color: "from-purple-500 to-purple-600",
      description: "Match with families who get your life - same chaos, similar interests, kids the same age. Our AI helps you find genuine connections nearby.",
      features: ["Smart family matching", "Location-based discovery", "Shared interests & values", "Children's age matching"]
    },
    {
      id: "sen",
      label: "SEN Corner",
      icon: Accessibility,
      color: "from-teal-500 to-teal-600",
      description: "Support for families with additional needs. Find Changing Places, accessible venues, and connect with families who understand.",
      features: ["Changing Places finder", "Accessibility info", "SEN-friendly venues", "Understanding community"]
    },
    {
      id: "activities",
      label: "Activities",
      icon: Calendar,
      color: "from-orange-500 to-orange-600",
      description: "Discover family-friendly activities, venues, and events near you. Filter by accessibility, age range, and interests.",
      features: ["Local venue listings", "Event calendar", "Accessibility filters", "Age-appropriate activities"],
      isDropdown: true,
      subItems: [
        {
          id: "find-venue",
          label: "Find a Venue",
          icon: MapPin,
          color: "from-purple-500 to-pink-500",
          description: "100+ soft plays, trampoline parks, farms, zoos and more with exclusive partner discounts!",
          features: ["100+ venues", "Partner discounts", "Filter by activity", "Accessibility info"]
        },
        {
          id: "activities-list",
          label: "Browse Activities",
          icon: Calendar,
          color: "from-orange-500 to-amber-500",
          description: "Swimming, gymnastics, arts & crafts, park meetups - find activities families are loving!",
          features: ["Popular activities", "Local events", "Group bookings", "Buddy UP! savings"]
        }
      ]
    },
    {
      id: "sen-focus",
      label: "SEN Focus",
      icon: Accessibility,
      color: "from-teal-500 to-cyan-500",
      description: "Dedicated support space for SEN families with resources, recommendations, and community guidance.",
      features: ["SEN recommendations", "Family experiences", "NHS guidance", "CWDT support"],
      isDropdown: true,
      subItems: [
        {
          id: "sen-zen",
          label: "SEN ZEN",
          icon: Heart,
          color: "from-teal-500 to-cyan-500",
          description: "A calming space dedicated to SEN families - resources, support, and understanding."
        },
        {
          id: "kikis-camp",
          label: "Kiki's Favourites",
          icon: Star,
          color: "from-purple-500 to-pink-500",
          description: "Kiera's curated recommendations, tips, and favourite resources for SEN families."
        }
      ]
    }
  ];

  // State to track which submenu is open
  const [activeSubMenu, setActiveSubMenu] = useState(null);

  return (
    <div className="min-h-screen bg-[#FFF9F5] overflow-hidden">
      {/* PWA Install Prompt */}
      <InstallPrompt />
      
      {/* Decorative blobs */}
      <div className="blob blob-purple w-96 h-96 -top-48 -right-48 fixed" />
      <div className="blob blob-pink w-80 h-80 top-1/3 -left-40 fixed" />
      <div className="blob blob-coral w-64 h-64 bottom-20 right-20 fixed" />

      {/* Events Ticker - Top of page, lighter color */}
      <EventsTicker />

      {/* Colorful Header Bar */}
      <header className="relative z-50 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 shadow-lg">
        <div className="max-w-7xl mx-auto">
          {/* Single header bar with logo, nav, and actions */}
          <div className="flex items-center justify-between px-6 py-3">
            {/* Logo - with more left spacing */}
            <Link to="/" className="flex items-center gap-3 mr-8">
              <img src={LOGO_URL} alt="KiddiPlay" className="w-10 h-10 rounded-full border-2 border-white/30" />
              <span className="text-xl font-bold text-white" style={{ fontFamily: 'Nunito, sans-serif' }}>
                KiddiPlay Club
              </span>
            </Link>
            
            {/* Navigation - all on one line */}
            <nav className="hidden md:flex items-center gap-1 flex-1">
              {/* About Us link */}
              <Link
                to="/about"
                className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium text-white/90 hover:bg-white/20 transition-all"
              >
                <Heart className="w-4 h-4" />
                About Us
              </Link>
              {/* Niamh's Corner */}
              <Link
                to="/niamhs-corner"
                className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium text-white/90 hover:bg-white/20 transition-all"
              >
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                Niamh&apos;s Corner
              </Link>
              {menuItems.map((item) => (
                <div key={item.id} className="relative">
                  <button
                    onClick={() => {
                      if (item.isDropdown) {
                        setActiveSubMenu(activeSubMenu === item.id ? null : item.id);
                        setActiveMenu(null);
                      } else {
                        setActiveMenu(activeMenu === item.id ? null : item.id);
                        setActiveSubMenu(null);
                      }
                    }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all ${
                      activeMenu === item.id || activeSubMenu === item.id
                        ? 'bg-white text-purple-600' 
                        : 'text-white/90 hover:bg-white/20'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${(activeMenu === item.id || activeSubMenu === item.id) ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              ))}
              
              {/* Partners button - same line */}
              <button 
                onClick={() => setShowPartnerModal(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium text-white/90 hover:bg-white/20 transition-all"
                data-testid="partners-btn"
              >
                Partners
              </button>
              
              {/* Login button - same line */}
              <Link to="/login">
                <Button className="bg-white text-purple-600 hover:bg-purple-50 rounded-full px-5 py-2 text-sm font-medium ml-2" data-testid="login-btn">
                  Log In
                </Button>
              </Link>
            </nav>

            {/* Social icons - far right */}
            <div className="hidden md:flex items-center gap-2">
              <a href="https://instagram.com/kiddiplay_uk" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white p-2" data-testid="instagram-link">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="mailto:team@kiddiplayclub.com" className="text-white/80 hover:text-white p-2" data-testid="email-link">
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Dropdown Panel for "Unique to Us" */}
        {activeSubMenu === "unique-to-us" && (
          <div className="absolute left-0 right-0 bg-white shadow-xl border-t border-purple-100 z-50">
            <div className="max-w-5xl mx-auto p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium">
                  <Gem className="w-4 h-4" />
                  Unique to Us
                </div>
                <p className="text-slate-600">We understand what features matter most to you!</p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {menuItems.find(m => m.id === "unique-to-us")?.subItems?.map(subItem => (
                  <Link key={subItem.id} to={`/${subItem.id === 'buddy-up' ? 'buddy-up' : subItem.id === 'mummamate' ? 'mummamate' : subItem.id === 'safety-hub' ? 'safety' : 'register'}`} className="block group">
                    <div className="bg-slate-50 rounded-2xl p-5 h-full hover:shadow-lg transition-all hover:-translate-y-1 border-2 border-transparent hover:border-purple-200">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${subItem.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                        <subItem.icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="font-bold text-slate-800 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                        {subItem.label}
                      </h3>
                      <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                        {subItem.description}
                      </p>
                      <span className={`inline-flex items-center gap-1 text-sm font-medium bg-gradient-to-r ${subItem.color} bg-clip-text text-transparent`}>
                        Learn More <ChevronRight size={14} className="text-purple-500" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
              <button 
                onClick={() => setActiveSubMenu(null)}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Dropdown Panel for "Activities" */}
        {activeSubMenu === "activities" && (
          <div className="absolute left-0 right-0 bg-white shadow-xl border-t border-purple-100 z-50">
            <div className="max-w-4xl mx-auto p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm font-medium">
                  <Calendar className="w-4 h-4" />
                  Activities
                </div>
                <p className="text-slate-600">Find things to do with your family!</p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {menuItems.find(m => m.id === "activities")?.subItems?.map(subItem => (
                  <Link key={subItem.id} to={subItem.id === 'find-venue' ? '/venues' : '/register'} className="block group">
                    <div className="bg-slate-50 rounded-xl p-4 h-full hover:shadow-lg transition-all hover:-translate-y-1 border-2 border-transparent hover:border-orange-200">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${subItem.color} flex items-center justify-center mb-2 group-hover:scale-110 transition-transform`}>
                        <subItem.icon className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-bold text-slate-800 mb-1 text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
                        {subItem.label}
                      </h3>
                      <p className="text-xs text-slate-600 line-clamp-2">
                        {subItem.description}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
              <button 
                onClick={() => setActiveSubMenu(null)}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Dropdown Panel for "SEN Focus" */}
        {activeSubMenu === "sen-focus" && (
          <div className="absolute left-0 right-0 bg-white shadow-xl border-t border-teal-100 z-50">
            <div className="max-w-4xl mx-auto p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-medium">
                  <Accessibility className="w-4 h-4" />
                  SEN Focus
                </div>
                <p className="text-slate-600">Dedicated support for SEN families</p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {menuItems.find(m => m.id === "sen-focus")?.subItems?.map(subItem => (
                  <Link key={subItem.id} to={`/${subItem.id}`} className="block group">
                    <div className="bg-slate-50 rounded-xl p-4 h-full hover:shadow-lg transition-all hover:-translate-y-1 border-2 border-transparent hover:border-teal-200">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${subItem.color} flex items-center justify-center mb-2 group-hover:scale-110 transition-transform`}>
                        <subItem.icon className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-bold text-slate-800 mb-1 text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
                        {subItem.label}
                      </h3>
                      <p className="text-xs text-slate-600 line-clamp-2">
                        {subItem.description}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
              <button 
                onClick={() => setActiveSubMenu(null)}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Dropdown Panel for regular menu items */}
        {activeMenu && !menuItems.find(m => m.id === activeMenu)?.isDropdown && (
          <div className="absolute left-0 right-0 bg-white shadow-xl border-t border-purple-100 z-50">
            <div className="max-w-4xl mx-auto p-8">
              {menuItems.filter(m => m.id === activeMenu && !m.isDropdown).map(item => (
                <div key={item.id} className="flex gap-8">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${item.color} text-white text-sm font-medium`}>
                        <item.icon className="w-4 h-4" />
                        {item.label}
                      </div>
                      {item.comingSoon && (
                        <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                          Coming Soon
                        </span>
                      )}
                    </div>
                    <p className="text-slate-600 text-lg leading-relaxed mb-6">
                      {item.description}
                    </p>
                    <ul className="space-y-2 mb-6">
                      {item.features.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-slate-700">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    {item.comingSoon ? (
                      <div className="flex flex-col gap-3">
                        <div className="flex gap-3 items-center">
                          <Button className={`bg-gradient-to-r ${item.color} text-white rounded-full px-6`}>
                            Notify Me + Get 5% Off
                          </Button>
                          <span className="text-sm text-violet-600 font-medium">🎁 Early bird voucher!</span>
                        </div>
                        <p className="text-sm text-slate-500">Sign up now and get a 5% discount code when we launch!</p>
                      </div>
                    ) : (
                      <div className="flex gap-3">
                        <Link to="/register">
                          <Button className={`bg-gradient-to-r ${item.color} text-white rounded-full px-6`}>
                            Get Started Free
                          </Button>
                        </Link>
                        <Link to="/login">
                          <Button variant="outline" className="rounded-full px-6">
                            I Have an Account
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>
                  <div className="hidden lg:block w-64">
                    <div className={`w-32 h-32 rounded-full bg-gradient-to-br ${item.color} opacity-20 mx-auto`} />
                  </div>
                </div>
              ))}
              <button 
                onClick={() => setActiveMenu(null)}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative z-10 px-6 py-12 md:py-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                UK-wide family matching
              </div>
              <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Building the Village
                <span className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 bg-clip-text text-transparent"> One Connection</span>
                <span> at a Time</span>
              </h1>
              <p className="text-xl text-slate-600 mb-6 leading-relaxed">
                Remember when neighbours knew each other and kids played out together? We&apos;re bringing that back. Connect with families nearby, arrange playdates, and build your support network.
              </p>
              <p className="text-lg text-purple-600 font-medium mb-8 flex items-center gap-2">
                <Heart className="w-5 h-5" fill="currentColor" />
                For parents who want more than just a playdate app
              </p>
              <div className="flex flex-row flex-wrap gap-3 items-center">
                <Link to="/login">
                  <Button className="hero-gradient text-white rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                    Get Started
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                {/* Static Flashing Download Button - matched size */}
                <button 
                  onClick={() => {
                    // Try to trigger PWA install or show instructions
                    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                    if (isIOS) {
                      alert('To install KiddiPlay:\n1. Tap the Share button below\n2. Select "Add to Home Screen"');
                    } else if (window.deferredPrompt) {
                      window.deferredPrompt.prompt();
                    } else {
                      alert('To install KiddiPlay, look for the install icon in your browser\'s address bar, or add this page to your home screen!');
                    }
                  }}
                  className="px-8 py-6 bg-gradient-to-r from-teal-400 to-cyan-500 text-white rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 animate-pulse-glow flex items-center gap-2"
                  data-testid="download-here-btn"
                >
                  <Download className="w-5 h-5" />
                  Download
                </button>
              </div>
              
              {/* Freemium Note */}
              <p className="text-sm text-slate-500 mt-6 max-w-md">
                <span className="font-semibold text-purple-600">FREE to start:</span> Unlimited matches + 1 FREE meetup. Upgrade for unlimited meetups, photos & premium features.
              </p>
            </div>
            <div className="relative animate-fade-in-up stagger-2">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets-lqy194kg.emergentagent.net/job_4791deb4-db22-4895-9e0a-21aa9f309cff/artifacts/n6shh097_ChatGPT%20Image%20Jul%2021%2C%202026%2C%2001_40_55%20AM.png"
                  alt="KiddiPlay Club - Families connecting together"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              {/* Floating card - Sticky note with pin tacks */}
              <div className="absolute top-2 -right-12 w-36 animate-gentle-pulse" style={{ transform: 'rotate(-8deg)' }}>
                {/* Left pin tack */}
                <div 
                  className="absolute -top-2 left-3 w-4 h-4 rounded-full z-10"
                  style={{ 
                    background: 'radial-gradient(circle at 30% 30%, #FF6B6B, #CC4444)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3), inset 0 1px 2px rgba(255,255,255,0.4)',
                  }}
                ></div>
                {/* Right pin tack */}
                <div 
                  className="absolute -top-2 right-3 w-4 h-4 rounded-full z-10"
                  style={{ 
                    background: 'radial-gradient(circle at 30% 30%, #FF6B6B, #CC4444)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3), inset 0 1px 2px rgba(255,255,255,0.4)',
                  }}
                ></div>
                {/* Pink/purple sticky note */}
                <div className="bg-gradient-to-br from-pink-200 via-purple-100 to-purple-200 p-3 rounded-sm mt-1" style={{ boxShadow: '4px 6px 14px rgba(0,0,0,0.2)' }}>
                  <div className="text-center">
                    <p className="text-xs font-bold text-purple-700 mb-1">IT&apos;S A MATCH!</p>
                    <Heart className="w-5 h-5 text-pink-500 mx-auto mb-1" fill="currentColor" />
                    <p className="text-xs font-semibold text-slate-700">The Johnsons</p>
                    <p className="text-[10px] text-purple-400">0.5 miles away</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Below hero - Star rating and Pillboxes in one clean row */}
          <div className="flex flex-wrap items-center justify-between gap-6 mt-12 max-w-7xl mx-auto">
            {/* Star rating */}
            <div className="flex items-center gap-4">
              <div className="flex -space-x-2">
                {[1,2,3,4].map((i) => (
                  <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 via-pink-400 to-orange-300 border-2 border-white" />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-0.5">
                  {[1,2,3,4,5].map((i) => (
                    <Star key={i} className="w-4 h-4 text-amber-400" fill="#FBBF24" />
                  ))}
                </div>
                <span className="text-sm text-slate-600">Families connecting across the UK</span>
              </div>
            </div>
            
            {/* Pillboxes */}
            <div className="flex flex-row gap-3">
              <Link to="/sen" className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium hover:bg-teal-100 transition-colors border border-teal-200">
                <Accessibility className="w-4 h-4" />
                Changing Places
              </Link>
              <Link to="/sen" className="inline-flex items-center gap-2 bg-purple-50 text-purple-700 px-4 py-2 rounded-full text-sm font-medium hover:bg-purple-100 transition-colors border border-purple-200">
                <Heart className="w-4 h-4" />
                Inclusivity
              </Link>
              <Link to="/safeguarding" className="inline-flex items-center gap-2 bg-pink-50 text-pink-700 px-4 py-2 rounded-full text-sm font-medium hover:bg-pink-100 transition-colors border border-pink-200">
                <Shield className="w-4 h-4" />
                Safe Meetups
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Connection Made For You - Above All Else */}
      <section className="relative z-10 px-6 py-10 bg-gradient-to-br from-purple-100 via-pink-50 to-orange-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-pink-200 text-pink-700 px-4 py-2 rounded-full text-sm font-medium mb-3">
              <Heart className="w-4 h-4" fill="currentColor" />
              Built for Friendships!
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-2 text-[#6B4D8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Connection Made For You
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto text-sm">
              More than playdates - we&apos;re building your complete family support network
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            {/* BuddyUp Card */}
            <Link to="/buddy-up" className="block group">
              <div className="bg-white rounded-xl p-4 h-full hover:shadow-lg transition-all hover:-translate-y-1 border border-orange-200">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                  <Baby className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-slate-800 mb-1" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  Buddy UP!
                </h3>
                <p className="text-xs text-slate-600 mb-2">
                  Team up for tutoring, languages, sports & more - split costs 50/50.
                </p>
                <span className="inline-flex items-center gap-1 text-orange-600 font-medium text-xs">
                  Start Sharing <ChevronRight size={12} />
                </span>
              </div>
            </Link>

            {/* MummaMate Card */}
            <Link to="/mummamate" className="block group">
              <div className="bg-white rounded-xl p-4 h-full hover:shadow-lg transition-all hover:-translate-y-1 border border-pink-200">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                  <Heart className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-slate-800 mb-1" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  MummaMate™
                </h3>
                <p className="text-xs text-slate-600 mb-2">
                  Need a lift? Pharmacy run? Families who&apos;ve got your back.
                </p>
                <span className="inline-flex items-center gap-1 text-pink-600 font-medium text-xs">
                  Get Support <ChevronRight size={12} />
                </span>
              </div>
            </Link>

            {/* Safety Hub Card */}
            <Link to="/safety" className="block group">
              <div className="bg-white rounded-xl p-4 h-full hover:shadow-lg transition-all hover:-translate-y-1 border border-teal-200">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-green-500 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-slate-800 mb-1" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  Safety Hub
                </h3>
                <p className="text-xs text-slate-600 mb-2">
                  Trusted Family Badges, check-ins & conflict resolution.
                </p>
                <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-xs">
                  Safety Features <ChevronRight size={12} />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Section - Community First - Moved Higher */}
      <section className="relative z-10 px-6 py-10 bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-xs font-medium mb-3">
                <Shield className="w-3 h-3" />
                Community First
              </div>
              <h2 className="text-2xl font-bold mb-3 text-[#6B4D8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
                A Community Built on Trust
              </h2>
              <p className="text-sm text-slate-600 mb-4">
                We know how precious your family is. Connect confidently with genuine families looking for friendship and support, just like you.
              </p>
              <div className="space-y-2">
                {[
                  { text: "Verified profiles", icon: CheckCircle },
                  { text: "Block anyone instantly", icon: AlertTriangle },
                  { text: "Meet in public first", icon: Users },
                  { text: "Contact details stay private", icon: Shield },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <item.icon className="w-4 h-4 text-teal-600 flex-shrink-0" />
                    <span className="text-sm text-slate-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1772523794495-57b712e32b34?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHw0fHxjaGlsZHJlbiUyMGZyaWVuZHMlMjBsYXVnaGluZyUyMHBsYXlpbmclMjB0b2dldGhlciUyMG91dGRvb3J8ZW58MHx8fHwxNzgyNjk3Nzk3fDA&ixlib=rb-4.1.0&q=85"
                alt="Two children laughing while playing together on grass"
                className="rounded-2xl shadow-xl w-full h-64 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Feature Cards - Compact */}
      <section className="relative z-10 px-6 py-10 bg-gradient-to-br from-purple-100/50 via-pink-50/50 to-orange-50/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6">
            <p className="text-sm text-purple-600 font-medium mb-1">Everything you need</p>
            <h2 className="text-2xl font-bold text-[#6B4D8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              More Than Just Another App
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {menuItems.map((item, i) => (
              <Link to="/login" key={i} className="block group">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-3 shadow-sm hover:shadow-md transition-all hover:-translate-y-1 cursor-pointer border border-white/50 text-center h-full">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform`}>
                    <item.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-800 text-xs" style={{ fontFamily: 'Nunito, sans-serif' }}>{item.label}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Carousel - Activities & Families */}
      <section className="relative z-10 px-6 py-8 overflow-hidden bg-gradient-to-br from-pink-100 via-purple-50 to-orange-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-4">
            <p className="text-purple-600 font-medium text-xs uppercase tracking-wider mb-1">Real families, real fun</p>
            <h2 className="text-xl font-bold text-[#6B4D8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              What Families Are Doing Together
            </h2>
          </div>
          
          {/* Carousel Container - More compact */}
          <div className="relative">
            <div className="flex gap-3 overflow-hidden">
              {CAROUSEL_IMAGES.map((image, index) => (
                <div 
                  key={index}
                  className="flex-shrink-0 w-full md:w-1/3 transition-all duration-500 ease-in-out"
                  style={{
                    transform: `translateX(-${carouselIndex * 100}%)`,
                    opacity: Math.abs(carouselIndex - index) <= 2 ? 1 : 0.5
                  }}
                >
                  <div className="relative rounded-xl overflow-hidden shadow-lg group">
                    <img 
                      src={image.url} 
                      alt={image.alt}
                      className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                      <p className="text-white font-medium text-sm">{image.caption}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Carousel Controls */}
            <button 
              onClick={() => setCarouselIndex((prev) => (prev - 1 + CAROUSEL_IMAGES.length) % CAROUSEL_IMAGES.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors z-10"
            >
              <ChevronLeft className="w-4 h-4 text-purple-600" />
            </button>
            <button 
              onClick={() => setCarouselIndex((prev) => (prev + 1) % CAROUSEL_IMAGES.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors z-10"
            >
              <ChevronRight className="w-4 h-4 text-purple-600" />
            </button>
            
            {/* Carousel Dots */}
            <div className="flex justify-center gap-2 mt-4">
              {CAROUSEL_IMAGES.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCarouselIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    carouselIndex === index ? 'bg-purple-500 w-6' : 'bg-purple-200'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Niamh's Corner - Play CEO Section - Moved up */}
      <section className="relative z-10 px-6 py-8 bg-gradient-to-br from-pink-50 via-purple-50 to-orange-50">
        <div className="max-w-5xl mx-auto">
          <NiamhsCorner isPreview={true} />
        </div>
      </section>

      {/* Popular Activities Section */}
      <section className="relative z-10 px-6 py-10 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-6">
            <p className="text-purple-600 font-medium text-xs uppercase tracking-wider mb-1">Get inspired</p>
            <h2 className="text-2xl font-bold text-[#6B4D8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Popular Activities to Try Together
            </h2>
          </div>
          
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {[
              { name: "Swimming", emoji: "🏊", popular: true },
              { name: "Gymnastics", emoji: "🤸", popular: true },
              { name: "Soft Play", emoji: "🎪", popular: true },
              { name: "Park Meetups", emoji: "🌳", popular: true },
              { name: "Arts & Crafts", emoji: "🎨", popular: true },
              { name: "Football", emoji: "⚽", popular: true },
            ].map((activity, i) => (
              <div 
                key={i} 
                className="bg-white rounded-xl p-3 text-center shadow-sm hover:shadow-md transition-all hover:-translate-y-1 cursor-pointer border border-slate-100"
              >
                <div className="text-2xl mb-1">{activity.emoji}</div>
                <p className="font-medium text-slate-800 text-xs">{activity.name}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-6">
            <Link to="/register">
              <Button className="hero-gradient text-white rounded-full px-6 py-2 text-sm">
                Join & Discover More
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Weekly Highlights Section */}
      {weeklyHighlights.length > 0 && (
        <section className="relative z-10 px-6 py-10 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-xs font-medium mb-2">
                <Star className="w-3 h-3" fill="currentColor" />
                Community Favourites
              </div>
              <h2 className="text-2xl font-bold mb-2 text-[#6B4D8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
                This Week&apos;s Highlights
              </h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {weeklyHighlights.slice(0, 3).map((post, i) => (
                <Link to="/register" key={post.post_id || i} className="block group">
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-all border border-slate-100 hover:-translate-y-1">
                    <div className="aspect-[4/3] relative overflow-hidden">
                      {post.photo_urls?.length > 0 ? (
                        <img 
                          src={post.photo_urls[0]} 
                          alt="Family activity" 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-purple-400 via-pink-400 to-orange-300 flex items-center justify-center">
                          <span className="text-4xl">
                            {post.activity_type === "playdate" ? "👫" :
                             post.activity_type === "park" ? "🌳" :
                             post.activity_type === "swimming" ? "🏊" : "✨"}
                          </span>
                        </div>
                      )}
                      <div className="absolute bottom-2 right-2 bg-white/95 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1 shadow-sm">
                        <Heart className="w-3 h-3 text-pink-500" fill="#EC4899" />
                        <span className="text-xs font-semibold text-slate-700">{post.like_count || 0}</span>
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="font-medium text-slate-800 line-clamp-1 text-sm">{post.caption}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            
            <div className="text-center mt-6">
              <Link to="/register">
                <Button className="hero-gradient text-white rounded-full px-6 py-2 text-sm">
                  Join to See More
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* FREE Healthy Menu Lead Magnet */}
      <section className="relative z-10 px-6 py-12 bg-gradient-to-br from-green-50 via-teal-50 to-cyan-50 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            {/* Left - Menu Preview with poster peek effect */}
            <div className="relative">
              <div className="relative overflow-hidden" style={{ transform: 'rotate(-2deg)' }}>
                {/* Menu poster frame */}
                <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-lg p-3 shadow-xl border-4 border-amber-200">
                  <div className="text-center mb-2">
                    <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">This Week&apos;s Menu</span>
                  </div>
                  {/* Show only top portion of menu - teaser */}
                  <div className="relative h-48 overflow-hidden rounded-lg">
                    <img 
                      src="https://customer-assets-lqy194kg.emergentagent.net/job_4791deb4-db22-4895-9e0a-21aa9f309cff/artifacts/v3yp2ow2_Suggested%20Kid%27s%20Meals-%20Kiddiplay.png"
                      alt="KiddiPlay Healthy Family Menu"
                      className="w-full object-cover object-top"
                    />
                    {/* Fade overlay to hide bottom */}
                    <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-amber-100 to-transparent"></div>
                  </div>
                  <p className="text-center text-xs text-amber-600 mt-2 italic">Get the full menu free...</p>
                </div>
                {/* FREE badge */}
                <div className="absolute -top-2 -right-2 bg-gradient-to-r from-green-500 to-teal-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg animate-pulse">
                  FREE!
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-2 -left-2 text-3xl">🥦</div>
              <div className="absolute -top-2 right-8 text-2xl">🍎</div>
            </div>

            {/* Right - CTA */}
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium mb-3">
                <Sparkles className="w-3 h-3" />
                Free Download
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-3 text-[#6B4D8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Kid-Friendly Weekly Menu
              </h2>
              <p className="text-sm text-slate-600 mb-3">
                Struggling with &quot;what&apos;s for dinner?&quot; Get our <span className="font-semibold text-teal-600">FREE family meal planner</span>:
              </p>
              <ul className="space-y-1 mb-4 text-slate-700 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  7 days of balanced, kid-approved meals
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Halal, Kosher, Veggie & Vegan options
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Allergy-friendly swaps included
                </li>
              </ul>
              
              {/* Email signup form */}
              <div className="bg-white rounded-xl p-4 shadow-lg border border-green-100">
                <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-2">
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    className="flex-1 h-10 rounded-lg border-green-200 focus:border-green-400 text-sm"
                    data-testid="menu-email-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={newsletterSubmitting}
                    className="h-10 px-4 bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white rounded-lg font-semibold text-sm whitespace-nowrap"
                    data-testid="menu-submit-btn"
                  >
                    {newsletterSubmitting ? "..." : "Send Me The Menu! 🥗"}
                  </Button>
                </form>
                <p className="text-xs text-slate-500 mt-2">
                  Plus weekly tips & events. Unsubscribe anytime!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Family Venues Section - Compact */}
      <section className="relative z-10 px-6 py-8 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-2 text-[#6B4D8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Family Venues Directory
          </h2>
          <p className="text-sm text-slate-600 mb-4">
            100+ soft plays, trampoline parks, farms, zoos & more with partner discounts!
          </p>
          <Link to="/venues">
            <Button className="hero-gradient text-white rounded-full px-6 py-2 text-sm">
              <MapPin className="w-4 h-4 mr-2" />
              Explore Venues
            </Button>
          </Link>
        </div>
      </section>

      {/* Partner Section - Compact */}
      <section className="relative z-10 px-6 py-10 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold mb-2 text-[#6B4D8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Partner With KiddiPlay
            </h2>
            <p className="text-sm text-slate-600">
              Reach thousands of UK families actively looking for activities and venues
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { icon: Building2, title: "Venues & Activities", desc: "List your family-friendly business" },
              { icon: Stethoscope, title: "NHS & Healthcare", desc: "Special NHS rates available" },
              { icon: GraduationCap, title: "SEN Providers", desc: "Tailored packages" }
            ].map((partner, i) => (
              <div key={i} className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all text-center">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-3 mx-auto">
                  <partner.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-slate-800 text-sm mb-1">{partner.title}</h3>
                <p className="text-xs text-slate-600">{partner.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-6">
            <Button 
              className="hero-gradient text-white rounded-full px-6 py-2 text-sm"
              onClick={() => setShowPartnerModal(true)}
            >
              Partnership Enquiries
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section - Compact */}
      <section className="relative z-10 px-6 py-10">
        <div className="max-w-3xl mx-auto">
          <div className="hero-gradient rounded-2xl p-8 text-center text-white shadow-xl">
            <img src={LOGO_URL} alt="KiddiPlay" className="w-14 h-14 rounded-full mx-auto mb-4 border-2 border-white/30" />
            <h2 className="text-2xl font-bold mb-2 text-white" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Ready to Find Your Village?
            </h2>
            <p className="text-sm opacity-90 mb-4">
              It&apos;s free to join. No awkward small talk required (yet).
            </p>
            <Link to="/register">
              <Button className="bg-white text-purple-600 hover:bg-purple-50 rounded-full px-6 py-2 text-sm font-semibold shadow-lg">
                Join KiddiPlay Club
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-12 bg-slate-800 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <Link to="/" className="flex items-center gap-2 mb-4">
                <img src={LOGO_URL} alt="KiddiPlay" className="w-10 h-10 rounded-full" />
                <span className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  KiddiPlay Club
                </span>
              </Link>
              <p className="text-slate-400 text-sm">
                Building the village, one connection at a time. Connecting UK families to find their people.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-white">For Families</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/register" className="hover:text-white transition-colors">Let&apos;s Match Up!</Link></li>
                <li><Link to="/buddy-up" className="hover:text-white transition-colors">Buddy UP!</Link></li>
                <li><Link to="/sen-focus" className="hover:text-white transition-colors">SEN Focus</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">Activities</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-white">For Partners</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><button onClick={() => setShowPartnerModal(true)} className="hover:text-white transition-colors">Venues & Activities</button></li>
                <li><button onClick={() => setShowPartnerModal(true)} className="hover:text-white transition-colors">NHS & Healthcare</button></li>
                <li><button onClick={() => setShowPartnerModal(true)} className="hover:text-white transition-colors">SEN Providers</button></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
                <li><span className="text-slate-500 cursor-not-allowed">KiddiPlay Shop (Coming Soon)</span></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-white">Connect</h4>
              <div className="flex items-center gap-4 mb-4">
                <a href="https://instagram.com/kiddiplay_uk" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center hover:bg-pink-500 transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="mailto:team@kiddiplayclub.com" className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center hover:bg-purple-500 transition-colors">
                  <Mail className="w-5 h-5" />
                </a>
              </div>
              <p className="text-slate-400 text-sm">@kiddiplay_uk</p>
              <p className="text-slate-400 text-sm mb-4">team@kiddiplayclub.com</p>
              
              {/* Newsletter Signup */}
              <div className="mt-4 pt-4 border-t border-slate-700">
                <h5 className="text-sm font-medium text-white mb-2">Stay in the loop</h5>
                <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Your email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white text-sm h-9"
                    data-testid="newsletter-email-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={newsletterSubmitting}
                    className="bg-teal-500 hover:bg-teal-600 h-9 px-3"
                    data-testid="newsletter-submit-btn"
                  >
                    {newsletterSubmitting ? "..." : <Mail className="w-4 h-4" />}
                  </Button>
                </form>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-700 pt-8">
            {/* Legal Links */}
            <div className="flex flex-wrap justify-center gap-4 mb-6 text-xs text-slate-400">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <span>|</span>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <span>|</span>
              <Link to="/indemnity" className="hover:text-white transition-colors">Indemnity & Liability</Link>
              <span>|</span>
              <Link to="/safeguarding" className="hover:text-white transition-colors">Safeguarding</Link>
              <span>|</span>
              <Link to="/accessibility" className="hover:text-white transition-colors">Accessibility</Link>
              <span>|</span>
              <Link to="/modern-slavery" className="hover:text-white transition-colors">Modern Slavery Statement</Link>
            </div>
            
            {/* Main Footer Text */}
            <div className="text-center text-slate-400 text-sm">
              <p>© 2025 KiddiPlay Club. All rights reserved.</p>
              <p className="mt-2">Made with ❤️ for families across the UK</p>
              <p className="mt-3 text-xs max-w-2xl mx-auto">
                KiddiPlay Club is a social platform connecting families. We do NOT provide childcare, childminding, or guardian services. 
                All meetups are arranged at parents&apos; own discretion and responsibility.
              </p>
              <p className="mt-2 text-xs text-slate-500">
                Registered with the Information Commissioner&apos;s Office (ICO) | For children aged 0-16 years
              </p>
              
              {/* International Expansion Interest */}
              <div className="mt-6 pt-4 border-t border-slate-700">
                <button 
                  onClick={() => setShowInternationalModal(true)}
                  className="inline-flex items-center gap-2 text-sm text-teal-400 hover:text-teal-300 transition-colors"
                  data-testid="international-interest-btn"
                >
                  <Globe className="w-4 h-4" />
                  Not in the UK? Register your interest for when we expand!
                </button>
              </div>
            </div>
            
            {/* Admin Link */}
            <div className="text-center mt-4">
              <Link to="/admin" className="text-xs text-slate-600 hover:text-slate-400 transition-colors">
                Admin
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Partner Registration Modal */}
      {showPartnerModal && (
        <PartnerModal onClose={() => setShowPartnerModal(false)} />
      )}

      {/* International Interest Modal */}
      {showInternationalModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 relative animate-fade-in">
            <button 
              onClick={() => setShowInternationalModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-teal-500 to-cyan-500 flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Coming Soon to Your Country!
              </h3>
              <p className="text-slate-600 mt-2">
                KiddiPlay is currently UK-only, but we&apos;re planning to expand. Register your interest and be the first to know!
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Your Email</label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={intlEmail}
                  onChange={(e) => setIntlEmail(e.target.value)}
                  className="h-12 rounded-xl"
                  data-testid="intl-email-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Where are you?</label>
                <select
                  value={intlCountry}
                  onChange={(e) => setIntlCountry(e.target.value)}
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="intl-country-select"
                >
                  <option value="">Select your country...</option>
                  {EXPANSION_COUNTRIES.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.flag} {country.name}
                    </option>
                  ))}
                  <option value="other">🌍 Other</option>
                </select>
              </div>

              <Button
                onClick={handleInternationalSubmit}
                disabled={intlSubmitting}
                className="w-full h-12 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-xl font-semibold"
                data-testid="intl-submit-btn"
              >
                {intlSubmitting ? "Registering..." : "Notify Me When You Launch!"}
              </Button>
            </div>

            <p className="text-center text-xs text-slate-500 mt-4">
              We&apos;ll only email you about launches in your selected country.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Partner Registration Modal Component
const PartnerModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    partnerType: "",
    businessName: "",
    contactName: "",
    email: "",
    phone: "",
    website: "",
    address: "",
    city: "",
    postcode: "",
    description: "",
    accessibilityFeatures: [],
    agreedToTerms: false
  });

  const partnerTypes = [
    { value: "venue", label: "Venue / Activity Provider", commission: "15% per booking" },
    { value: "corporate", label: "Corporate Partner", commission: "Custom package" },
    { value: "nhs", label: "NHS / Healthcare", commission: "Reduced rates available" },
    { value: "sen_provider", label: "SEN Service Provider", commission: "Tailored packages" },
    { value: "charity", label: "Charity / Non-profit", commission: "Free or reduced" }
  ];

  const accessibilityOptions = [
    "Wheelchair accessible",
    "Changing Places toilet",
    "Sensory-friendly sessions",
    "Quiet spaces available",
    "Staff SEN trained",
    "Visual aids/PECS friendly",
    "Hearing loop"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const API = process.env.REACT_APP_BACKEND_URL + '/api';
      await axios.post(`${API}/partner-enquiry`, formData);
      alert("Thank you for your interest! Our partnerships team will be in touch soon to discuss how we can work together.");
      onClose();
    } catch (error) {
      console.error('Partner form error:', error);
      alert("Thank you for your interest! Our partnerships team will be in touch soon to discuss how we can work together.");
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Become a Partner
              </h2>
              <p className="text-white/80 text-sm">Reach thousands of UK families</p>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Partner Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              What type of partner are you? *
            </label>
            <select
              required
              value={formData.partnerType}
              onChange={(e) => setFormData({...formData, partnerType: e.target.value})}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select partner type...</option>
              {partnerTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label} - {type.commission}
                </option>
              ))}
            </select>
          </div>

          {/* Commission Info */}
          {formData.partnerType && (
            <div className="bg-purple-50 rounded-xl p-4">
              <p className="text-purple-800 text-sm">
                <strong>Commission structure:</strong> {partnerTypes.find(t => t.value === formData.partnerType)?.commission}
              </p>
              {formData.partnerType === "venue" && (
                <p className="text-purple-600 text-xs mt-1">
                  15% commission on all bookings made through KiddiPlay. No upfront costs, no monthly fees.
                </p>
              )}
              {formData.partnerType === "nhs" && (
                <p className="text-purple-600 text-xs mt-1">
                  Special rates for NHS trusts and healthcare providers. Contact us to discuss your needs.
                </p>
              )}
              {formData.partnerType === "sen_provider" && (
                <p className="text-purple-600 text-xs mt-1">
                  Tailored packages for SEN service providers. Priority listing in our SEN Corner.
                </p>
              )}
            </div>
          )}

          {/* Business Details */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Business Name *</label>
              <input
                type="text"
                required
                value={formData.businessName}
                onChange={(e) => setFormData({...formData, businessName: e.target.value})}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Your business name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contact Name *</label>
              <input
                type="text"
                required
                value={formData.contactName}
                onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Your name"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="email@business.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="07xxx xxxxxx"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Website</label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({...formData, website: e.target.value})}
              className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="https://www.yourbusiness.com"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">City/Town *</label>
              <input
                type="text"
                required
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="London"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Postcode</label>
              <input
                type="text"
                value={formData.postcode}
                onChange={(e) => setFormData({...formData, postcode: e.target.value})}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="SW1A 1AA"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Tell us about your business *</label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={3}
              className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              placeholder="What services do you offer? Who are your typical customers?"
            />
          </div>

          {/* Accessibility Features */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Accessibility Features (select all that apply)
            </label>
            <div className="grid grid-cols-2 gap-2">
              {accessibilityOptions.map(option => (
                <label key={option} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.accessibilityFeatures.includes(option)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({...formData, accessibilityFeatures: [...formData.accessibilityFeatures, option]});
                      } else {
                        setFormData({...formData, accessibilityFeatures: formData.accessibilityFeatures.filter(f => f !== option)});
                      }
                    }}
                    className="w-4 h-4 rounded border-slate-300 text-purple-500 focus:ring-purple-500"
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>

          {/* Terms */}
          <div className="bg-slate-50 rounded-xl p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                required
                checked={formData.agreedToTerms}
                onChange={(e) => setFormData({...formData, agreedToTerms: e.target.checked})}
                className="w-5 h-5 mt-0.5 rounded border-slate-300 text-purple-500 focus:ring-purple-500"
              />
              <span className="text-sm text-slate-600">
                I understand that KiddiPlay is a family connection platform and does NOT provide childcare or childminding services. 
                Partners are responsible for their own safeguarding, insurance, and compliance. *
              </span>
            </label>
          </div>

          <Button
            type="submit"
            className="w-full hero-gradient text-white rounded-xl py-4 text-lg font-medium"
          >
            Submit Partnership Application
          </Button>

          <p className="text-xs text-center text-slate-500">
            We&apos;ll review your application and a member of our team will be in touch.
          </p>
        </form>
      </div>
    </div>
  );
};

export default Landing;
